import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import Footer from './Footer';
import './BlogPost.css';
import './PublicLanding.css';

function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`/api/blog/${slug}`);
        setPost(response.data);
      } catch (error) {
        console.error('Error fetching blog post:', error);
        toast.error('Статья не найдена');
        navigate('/blog');
      } finally {
        setLoading(false);
      }
    };

    if (slug) {
      fetchPost();
    }
  }, [slug, navigate]);

  // Обновление метатегов для соцсетей (Open Graph, Twitter Card)
  useEffect(() => {
    if (!post) return;

    const siteUrl = window.location.origin; // https://pikly.ru
    const postUrl = `${siteUrl}/blog/${post.slug}`;
    
    // Нормализуем URL изображения (убеждаемся, что это абсолютный URL)
    let imageUrl = post.image_url;
    if (imageUrl) {
      // Если URL относительный, добавляем домен
      if (!imageUrl.startsWith('http')) {
        // Убираем начальный слеш, если есть, чтобы избежать двойных слешей
        imageUrl = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`;
        imageUrl = `${siteUrl}${imageUrl}`;
      }
    } else {
      // Fallback на иконку сайта
      imageUrl = `${siteUrl}/logo.png`;
    }
    
    const description = post.excerpt || post.subtitle || post.title || 'Статья на Pikly Platform';
    const title = post.title || 'Pikly Platform';

    // Функция для создания/обновления метатега
    const setMetaTag = (property, content) => {
      if (!content) return; // Пропускаем пустые значения
      
      let element = document.querySelector(`meta[property="${property}"]`) || 
                    document.querySelector(`meta[name="${property}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        if (property.startsWith('og:') || property.startsWith('twitter:')) {
          element.setAttribute('property', property);
        } else {
          element.setAttribute('name', property);
        }
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Open Graph метатеги (для Facebook, Telegram, VK и др.)
    setMetaTag('og:type', 'article');
    setMetaTag('og:title', title);
    setMetaTag('og:description', description);
    setMetaTag('og:image', imageUrl);
    setMetaTag('og:url', postUrl);
    setMetaTag('og:site_name', 'Pikly Platform');
    setMetaTag('og:locale', 'ru_RU');
    
    // Дополнительные метатеги для статьи
    if (post.author) {
      setMetaTag('article:author', post.author);
    }
    if (post.published_at) {
      const publishedDate = new Date(post.published_at).toISOString();
      setMetaTag('article:published_time', publishedDate);
    }

    // Twitter Card метатеги
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', imageUrl);

    // Стандартные метатеги
    setMetaTag('description', description);
    
    // Обновляем title страницы
    document.title = `${title} | Pikly Platform`;

    // Очистка при размонтировании компонента
    return () => {
      // Можно удалить метатеги, но обычно их оставляют для SEO
      document.title = 'Pikly Platform';
    };
  }, [post]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const sanitizeHTML = (html) => {
    // Проверяем, что мы в браузере
    if (typeof document === 'undefined') {
      return html;
    }
    
    // Базовая санитизация HTML - разрешаем только безопасные теги
    // Для более строгой санитизации можно использовать библиотеку DOMPurify
    const allowedTags = [
      'p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'code', 'pre',
      'table', 'thead', 'tbody', 'tr', 'th', 'td', 'div', 'span'
    ];
    
    // Создаем временный элемент для парсинга HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    
    // Удаляем все неразрешенные теги
    const walker = document.createTreeWalker(
      tempDiv,
      NodeFilter.SHOW_ELEMENT,
      null
    );
    
    const nodesToRemove = [];
    let node;
    while (node = walker.nextNode()) {
      const tagName = node.tagName.toLowerCase();
      
      if (!allowedTags.includes(tagName)) {
        nodesToRemove.push(node);
        continue;
      }
      
      // Удаляем опасные атрибуты у разрешенных тегов
      if (tagName === 'a') {
        // Разрешаем только href и безопасные атрибуты
        Array.from(node.attributes).forEach(attr => {
          if (!['href', 'target', 'rel'].includes(attr.name)) {
            node.removeAttribute(attr.name);
          }
        });
        // Убеждаемся, что внешние ссылки открываются безопасно
        const href = node.getAttribute('href');
        if (href && !href.startsWith('#') && !href.startsWith('javascript:')) {
          node.setAttribute('target', '_blank');
          node.setAttribute('rel', 'noopener noreferrer');
        }
      }
      
      if (tagName === 'img') {
        // Разрешаем только безопасные атрибуты для изображений
        Array.from(node.attributes).forEach(attr => {
          if (!['src', 'alt', 'style', 'class'].includes(attr.name)) {
            node.removeAttribute(attr.name);
          }
        });
        // Убеждаемся, что src не содержит опасных протоколов
        const src = node.getAttribute('src');
        if (src && (src.startsWith('javascript:') || src.startsWith('data:text/html'))) {
          node.removeAttribute('src');
        }
      }
      
      // Удаляем все атрибуты, начинающиеся с on* (события)
      Array.from(node.attributes).forEach(attr => {
        if (attr.name.toLowerCase().startsWith('on')) {
          node.removeAttribute(attr.name);
        }
      });
    }
    
    // Удаляем неразрешенные узлы
    nodesToRemove.forEach(node => {
      const parent = node.parentNode;
      if (parent) {
        // Заменяем удаляемый узел его содержимым
        while (node.firstChild) {
          parent.insertBefore(node.firstChild, node);
        }
        parent.removeChild(node);
      }
    });
    
    return tempDiv.innerHTML;
  };

  const formatContent = (content) => {
    // Проверяем, является ли контент HTML (содержит HTML теги)
    const isHTML = /<[a-z][\s\S]*>/i.test(content);
    
    if (isHTML) {
      // Если это HTML, санитизируем и рендерим напрямую
      const sanitizedHTML = sanitizeHTML(content);
      return (
        <div 
          className="blog-content-html"
          dangerouslySetInnerHTML={{ __html: sanitizedHTML }}
        />
      );
    }
    
    // Иначе обрабатываем как markdown (старая логика для обратной совместимости)
    // Убираем горизонтальные разделители (---)
    let processedContent = content.replace(/^---+\s*$/gm, '');
    
    // Разбиваем на параграфы (группируем строки между пустыми строками)
    const paragraphs = [];
    let currentParagraph = [];
    
    processedContent.split('\n').forEach((line, index) => {
      const trimmed = line.trim();
      
      // Заголовки - отдельные элементы
      if (trimmed.startsWith('# ')) {
        if (currentParagraph.length > 0) {
          paragraphs.push({ type: 'paragraph', content: currentParagraph.join(' ') });
          currentParagraph = [];
        }
        paragraphs.push({ type: 'h1', content: trimmed.substring(2) });
        return;
      }
      if (trimmed.startsWith('## ')) {
        if (currentParagraph.length > 0) {
          paragraphs.push({ type: 'paragraph', content: currentParagraph.join(' ') });
          currentParagraph = [];
        }
        paragraphs.push({ type: 'h2', content: trimmed.substring(3) });
        return;
      }
      if (trimmed.startsWith('### ')) {
        if (currentParagraph.length > 0) {
          paragraphs.push({ type: 'paragraph', content: currentParagraph.join(' ') });
          currentParagraph = [];
        }
        paragraphs.push({ type: 'h3', content: trimmed.substring(4) });
        return;
      }
      
      // Пустая строка - завершаем параграф
      if (trimmed === '') {
        if (currentParagraph.length > 0) {
          paragraphs.push({ type: 'paragraph', content: currentParagraph.join(' ') });
          currentParagraph = [];
        }
        return;
      }
      
      // Добавляем строку в текущий параграф
      currentParagraph.push(trimmed);
    });
    
    // Добавляем последний параграф, если есть
    if (currentParagraph.length > 0) {
      paragraphs.push({ type: 'paragraph', content: currentParagraph.join(' ') });
    }
    
    // Рендерим параграфы
    return paragraphs.map((para, index) => {
      if (para.type === 'h1') {
        return <h1 key={index} className="blog-content-h1">{para.content}</h1>;
      }
      if (para.type === 'h2') {
        return <h2 key={index} className="blog-content-h2">{para.content}</h2>;
      }
      if (para.type === 'h3') {
        return <h3 key={index} className="blog-content-h3">{para.content}</h3>;
      }
      
      // Обрабатываем markdown в параграфах
      let text = para.content;
      const elements = [];
      let lastIndex = 0;
      
      // Обрабатываем жирный текст **text**
      const boldRegex = /\*\*(.+?)\*\*/g;
      const linkRegex = /(https?:\/\/[^\s]+)/g;
      
      // Сначала находим все совпадения
      const matches = [];
      let match;
      
      // Находим жирный текст
      while ((match = boldRegex.exec(text)) !== null) {
        matches.push({
          type: 'bold',
          start: match.index,
          end: match.index + match[0].length,
          content: match[1]
        });
      }
      
      // Находим ссылки
      linkRegex.lastIndex = 0;
      while ((match = linkRegex.exec(text)) !== null) {
        matches.push({
          type: 'link',
          start: match.index,
          end: match.index + match[0].length,
          content: match[0]
        });
      }
      
      // Сортируем совпадения по позиции
      matches.sort((a, b) => a.start - b.start);
      
      // Удаляем перекрывающиеся совпадения (приоритет жирному тексту)
      const filteredMatches = [];
      for (let i = 0; i < matches.length; i++) {
        const current = matches[i];
        let overlaps = false;
        for (let j = 0; j < filteredMatches.length; j++) {
          const existing = filteredMatches[j];
          if (
            (current.start < existing.end && current.end > existing.start) ||
            (current.start >= existing.start && current.end <= existing.end)
          ) {
            overlaps = true;
            break;
          }
        }
        if (!overlaps) {
          filteredMatches.push(current);
        }
      }
      
      // Строим элементы
      const parts = [];
      let currentPos = 0;
      
      filteredMatches.forEach((match) => {
        // Добавляем текст до совпадения
        if (match.start > currentPos) {
          parts.push({ type: 'text', content: text.substring(currentPos, match.start) });
        }
        
        // Добавляем совпадение
        if (match.type === 'bold') {
          parts.push({ type: 'bold', content: match.content });
        } else if (match.type === 'link') {
          parts.push({ type: 'link', content: match.content, url: match.content });
        }
        
        currentPos = match.end;
      });
      
      // Добавляем оставшийся текст
      if (currentPos < text.length) {
        parts.push({ type: 'text', content: text.substring(currentPos) });
      }
      
      // Если нет совпадений, просто текст
      if (parts.length === 0) {
        parts.push({ type: 'text', content: text });
      }
      
      return (
        <p key={index} className="blog-content-p">
          {parts.map((part, partIndex) => {
            if (part.type === 'bold') {
              return <strong key={partIndex}>{part.content}</strong>;
            }
            if (part.type === 'link') {
              return (
                <a 
                  key={partIndex} 
                  href={part.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  {part.content}
                </a>
              );
            }
            return <span key={partIndex}>{part.content}</span>;
          })}
        </p>
      );
    });
  };

  if (loading) {
    return (
      <div className="blog-post-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  const content = (
    <>
      {/* Публичный хедер для неавторизованных */}
      {!user && (
        <header className="public-header">
          <div className="public-logo" onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
            <img src="/logo.png" alt="Pikly" />
            <span>PIKLY</span>
          </div>
          <div className="public-actions">
            <button className="btn ghost" onClick={() => navigate('/register')}>Регистрация</button>
            <button className="btn primary" onClick={() => navigate('/login')}>Вход</button>
          </div>
        </header>
      )}
      
      <div className="blog-post-page">
        <div className="blog-post-container">
          <button 
            className="blog-post-back"
            onClick={() => {
              // Если пользователь авторизован, идем в блог, иначе на главную
              if (user) {
                navigate('/blog');
              } else {
                navigate('/blog');
              }
            }}
          >
            <ArrowLeft size={20} />
            К статьям
          </button>

        {post.image_url && (
          <div className="blog-post-header-image">
            <img 
              src={post.image_url} 
              alt={post.title}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          </div>
        )}

        <article className="blog-post-article">
          <header className="blog-post-header">
            <h1 className="blog-post-title">{post.title}</h1>
            {post.subtitle && (
              <p className="blog-post-subtitle">{post.subtitle}</p>
            )}
            <div className="blog-post-meta">
              {post.author && (
                <div className="blog-post-author">
                  <User size={18} />
                  <span>{post.author}</span>
                </div>
              )}
              {post.published_at && (
                <div className="blog-post-date">
                  <Calendar size={18} />
                  <span>{formatDate(post.published_at)}</span>
                </div>
              )}
            </div>
          </header>

          <div className="blog-post-content">
            {formatContent(post.content)}
          </div>
        </article>
        </div>
      </div>
      
      {/* Публичный футер для неавторизованных */}
      {!user && <Footer />}
    </>
  );

  // Если пользователь авторизован, возвращаем без обертки public-landing (используется внутри Dashboard)
  if (user) {
    return content;
  }

  // Если не авторизован, оборачиваем в public-landing для публичного вида
  return <div className="public-landing">{content}</div>;
}

export default BlogPost;
